import type { InstantSearch } from "instantsearch.js";
import { RANGE_META } from "@/services/algoliaRouting.core";
import { rangeFiltersState, clearRangeFilter } from "@/services/algoliaRouting.blade";
import { clearSpecificationFilter } from "./specification-search";
import { escapeHtml, formatNumber } from "./helpers";
import { isCuratedLayoutPage } from "../curated-layout";

const RANGE_FILTER_PILL_CLOSE_ICON =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="current-refinements__item__close" aria-hidden="true"><path d="M3.81202 3L3 3.81202L7.18798 8L3 12.188L3.81202 13L8 8.81202L12.188 13L13 12.188L8.81202 8L13 3.81202L12.188 3L8 7.18798L3.81202 3Z" fill="currentColor"></path></svg>';

const RANGE_LABELS: Record<string, string> = {
    monthly_price: "Mensualité",
    mileage: "Kilométrage",
    price: "Prix",
    registration_at: "Année",
    weight: "Poids",
    co2_emission: "CO2",
    power: "Puissance",
};

export function buildRangePillLabel(
    attribute: string,
    range: { min?: number; max?: number },
    unit: string,
): string | null {
    const label = RANGE_LABELS[attribute] ?? attribute;
    const { min, max } = range;

    if (min !== undefined && max !== undefined) {
        const minVal =
            attribute === "registration_at" ? String(min) : formatNumber(min);
        const maxVal =
            attribute === "registration_at" ? String(max) : formatNumber(max);
        return `${label}: ${minVal}${unit} - ${maxVal}${unit}`;
    }
    if (min !== undefined) {
        const minVal =
            attribute === "registration_at" ? String(min) : formatNumber(min);
        return `${label}: ≥ ${minVal}${unit}`;
    }
    if (max !== undefined) {
        const maxVal =
            attribute === "registration_at" ? String(max) : formatNumber(max);
        return `${label}: ≤ ${maxVal}${unit}`;
    }

    return null;
}

function renderRangeFilterPills(container: HTMLElement): void {
    const pills: string[] = [];
    // CurrentRefinement.vue renders its custom filter chips as <a> without href (out of
    // the tab order): kept on the curated page, which mirrors Vehicle/Index.
    const href = isCuratedLayoutPage() ? "" : ' href="#"';
    const spec = rangeFiltersState.value.specification?.trim();

    if (spec) {
        pills.push(`
            <a${href} class="current-refinements__item" data-clear="specification">
                <span>Finition: ${escapeHtml(spec)}</span>
                ${RANGE_FILTER_PILL_CLOSE_ICON}
            </a>
        `);
    }

    for (const { attribute, unit } of RANGE_META) {
        const range = rangeFiltersState.value[attribute];
        if (!range) continue;

        const text = buildRangePillLabel(attribute, range, unit);
        if (!text) continue;

        pills.push(`
            <a${href} class="current-refinements__item" data-clear="${attribute}">
                <span>${escapeHtml(text)}</span>
                ${RANGE_FILTER_PILL_CLOSE_ICON}
            </a>
        `);
    }

    container.innerHTML = pills.join("");
    container.hidden = pills.length === 0;
}

/**
 * Renders range / specification filter pills in the marketplace header.
 * Mirrors Vue CurrentRefinement custom filters (configure-based ranges on curated pages).
 */
export function createRangeFiltersPillsWidget(
    containerSelector: string,
    searchInstance: InstantSearch,
) {
    const container = document.querySelector(
        containerSelector,
    ) as HTMLElement | null;
    if (!container) return null;

    const paint = () => renderRangeFilterPills(container);

    container.addEventListener("click", (event) => {
        const target = (event.target as HTMLElement).closest<HTMLElement>(
            "[data-clear]",
        );
        if (!target) return;
        event.preventDefault();

        const key = target.dataset.clear;
        if (key === "specification") {
            clearSpecificationFilter();
        } else if (key) {
            clearRangeFilter(
                key as keyof Omit<
                    typeof rangeFiltersState.value,
                    "specification"
                >,
            );
        }
    });

    searchInstance.on("render", paint);
    paint();

    return {
        $$type: "custom.rangeFiltersPills",
        dispose() {
            searchInstance.removeListener("render", paint);
        },
    };
}
